import { sendGraphQLQuery } from '@/services/gql-util';
import {Media,MediaFilters,MediaListResponse,MediaOptions} from "@/types/Media";



export function useMedia() {

	const setModerationStatus = async (mediaId: number,status:number, note?: string) : Promise<Boolean> => {
		const mutation = `
		mutation ($mediaId: Int!, $status: Int!, $note: String) { AssetMutations {
			setModerationStatus(mediaId: $mediaId, status: $status, note: $note) 
		}
		}
		`;
		return sendGraphQLQuery(mutation, { mediaId, status, note});
	}

	const trashFile = async (mediaId: number,recursive?:boolean): Promise<Boolean> => {
		const mutation = `
		mutation ($mediaId: Int!, $recursive: Boolean) { AssetMutations {
			trashFile(mediaId: $mediaId, recursive: $recursive) 
		}
		}
		`;
		return sendGraphQLQuery(mutation, { mediaId, recursive});
	}
	const fetchMedia = async ( vhost: number, filters: MediaFilters, sort: string = 'UPLOAD DESC', start: number = 0, limit: number = 25, fields: (keyof Media)[], options: MediaOptions = {}): Promise<MediaListResponse> => {
		const processedFields = fields.map(field =>
						   field === 'groups' ? 'groups { id name }' :
							   field === 'parent_info' ? 'parent_info { id title }' :
							   field === 'externalids' ? 'externalids { external_id external_id_provider }' :
							   field
						  ).join('\n');
						  const query = `
						  query mediaList (
							  $vhost: Int!,
							  $filters: MediaFilters,
							  $sort: String,
							  $start: Int,
							  $limit: Int,
							  $options: [MediaOptions]
						  ) {
							  Media {
								  mediaList(
									  vhost: $vhost,
									  filters: $filters,
									  sort: $sort,
									  start: $start,
									  limit: $limit,
									  options: $options
								  ) {
									  items {
									  ${processedFields}
									  }
									  totalCount
								  }
							  }
						  }
						  `;

						  const formattedFilters = {
							  ...filters,
							  // Ensure array fields are properly formatted (only if present)
							  ...(filters.status != null ? { status: Array.isArray(filters.status) ? filters.status : [filters.status] } : {}),
							  ...(filters.filetype != null ? { filetype: Array.isArray(filters.filetype) ? filters.filetype : [filters.filetype] } : {})
						  };

						  // Clean null/undefined values and empty arrays
						  const cleanedFilters = Object.fromEntries(
							  Object.entries(formattedFilters).filter(([_, value]) => {
								  if (value === null || value === undefined || value === '') return false;
								  if (Array.isArray(value) && (value.length === 0 || value.every(v => v == null))) return false;
								  return true;
							  })
						  );

						  if (typeof cleanedFilters.channel !== 'undefined' && cleanedFilters.channel === 0) {
							  delete cleanedFilters.channel;
						  }

						  // Properly format options
						  const formattedOptions = typeof options === 'number' ? { want_status: options } : options;

						  const variables = {
							  vhost,
							  filters: cleanedFilters,
							  sort: sort || undefined,
							  start,
							  limit,
							  options: [formattedOptions]
						  };

						  const response = await sendGraphQLQuery(query, variables);
						  return {
							  mediaData: response.Media.mediaList.items,
							  totalCount: response.Media.mediaList.totalCount 
						  };
	}
	const getFileInfo = async (mediaId: number): Promise<Media> => {
		const query = `
		query ($id: Int!) {
			Media {
				media(id: $id) {
					id title message upload filetype extension status
					thumbUrl publicUrl contenttype uid metadata tags
					groups { id name }
					externalids { external_id external_id_provider }
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Media: { media: Media } }>(query, { id: mediaId });
		return data.Media.media;
	};

	const updateFile = async (mediaId: number, fileData: Record<string, any>): Promise<boolean> => {
		const mutation = `
		mutation ($mediaId: Int!, $fileData: MediaUpdateInput!) {
			AssetMutations {
				updateFile(mediaId: $mediaId, fileData: $fileData)
			}
		}`;
		const data = await sendGraphQLQuery<{ AssetMutations: { updateFile: boolean } }>(mutation, { mediaId, fileData });
		return data?.AssetMutations?.updateFile ?? false;
	};

	/**
	 * Get full media detail with all metadata, versions, status info, etc.
	 * Replaces the legacy media.getFileInfo JSON service call.
	 */
	const getMediaDetail = async (mediaId: number, version: string = ''): Promise<any> => {
		const query = `
		query ($id: Int!, $version: String) {
			Media {
				mediaDetail(id: $id, version: $version)
			}
		}`;
		const data = await sendGraphQLQuery<{ Media: { mediaDetail: any } }>(query, { id: mediaId, version });
		return data?.Media?.mediaDetail;
	};

	const queueReconversion = async (mediaId: number): Promise<boolean> => {
		const mutation = `
		mutation ($mediaId: Int!) { AssetMutations {
			queueReconversion(mediaId: $mediaId)
		}
		}
		`;
		const data = await sendGraphQLQuery<{ AssetMutations: { queueReconversion: boolean } }>(mutation, { mediaId });
		return data?.AssetMutations?.queueReconversion ?? false;
	};

	return {
		setModerationStatus,
		trashFile,
		fetchMedia,
		getFileInfo,
		getMediaDetail,
		updateFile,
		queueReconversion,
	};
}
